import { useState, useRef, useLayoutEffect, useCallback, useMemo, useEffect } from "react"
import PropTypes from "prop-types"
import { IconButton, CircularProgress, Badge } from "@mui/material"
import { CloudDownload } from "@mui/icons-material"
import { downloadXlsx } from "helpers/csv"
import Tooltip from "components/material/tooltip"
import { useAnalytics } from "connected-components/analytics"
import { downloadRegistry } from "connected-components/dashboard/context/download-registry"
import { useNotifications } from "hooks/notifications"
import { DASHBOARD_EVENTS } from "../../analytics"
import { getDownloadDataFileName } from "../../helpers/download"

export default function DashboardDownload({ enterDelay = 300, instrumentMetadata, isIntradayEnabled, latestPriceTimestamp, disabled, reduceDownloadData }) {
  const buttonRef = useRef()
  const [loading, setLoading] = useState(false)
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 })
  const [downloadIncludesFlattenedSheet, setDownloadIncludesFlattenedSheet] = useState(() => downloadRegistry.getHasAnyShouldFlatten())
  const analytics = useAnalytics()
  const notifications = useNotifications()

  useLayoutEffect(() => {
    if (buttonRef.current) {
      setDimensions({
        width: buttonRef.current.offsetWidth,
        height: buttonRef.current.offsetHeight,
      })
    }
  }, [])

  useEffect(() => {
    function handleShouldFlattenChange() {
      setDownloadIncludesFlattenedSheet(downloadRegistry.getHasAnyShouldFlatten())
    }

    const unsubscribe = downloadRegistry.subscribeShouldFlatten(handleShouldFlattenChange)

    // Prevent missing an emit that happened before subscription was attached.
    handleShouldFlattenChange()

    return unsubscribe
  }, [])

  const fileName = getDownloadDataFileName(instrumentMetadata, isIntradayEnabled, latestPriceTimestamp)
  const type = "csv"
  const title = useMemo(() => {
    let text = "Download as XLSX"
    if (downloadIncludesFlattenedSheet) {
      text += " - The downloaded file will be flattened to include each group's constituents split by attribute."
    }
    return text
  }, [downloadIncludesFlattenedSheet])

  const handleDownload = useCallback(async () => {
    setLoading(true)
    try {
      const downloadData = reduceDownloadData?.()
      if (downloadData) {
        const success = await downloadXlsx(fileName, downloadData, { type, track: () => analytics.track(DASHBOARD_EVENTS.DATA_DOWNLOADED, { format: "xlsx" }) })
        if (!success) {
          throw new Error()
        }
      }
    } catch {
      notifications.error("Download failed. Please try again.")
    } finally {
      setLoading(false)
    }
  }, [fileName, analytics, reduceDownloadData, notifications])

  return <div className="excel-download">
    <Tooltip enterDelay={enterDelay} title={title}>
      <span>
        {loading && <CircularProgress size={dimensions.width} />}
        <IconButton
          ref={buttonRef}
          disabled={loading || disabled}
          onClick={handleDownload}
          size="large"
        >
          <Badge color="primary" variant="dot" invisible={!downloadIncludesFlattenedSheet}>
            <CloudDownload />
          </Badge>
        </IconButton>
      </span>
    </Tooltip>
  </div>
}

DashboardDownload.propTypes = {
  instrumentMetadata: PropTypes.shape({
    name: PropTypes.string,
    id: PropTypes.string.isRequired,
  }).isRequired,
  enterDelay: PropTypes.number,
  isIntradayEnabled: PropTypes.bool,
  latestPriceTimestamp: PropTypes.string,
  disabled: PropTypes.bool,
  reduceDownloadData: PropTypes.func,
}
